// src/components/Reports/widgets/BurndownChartWidget.tsx

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MoreVertical } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import DashboardReportService from '@services/DashboardReportService';
import type {
  ReportWidget,
  ReportFilters,
  BurndownData,
  LayerData,
  NewSettings,
} from '@services/DashboardReportService';
import { ReportDatePicker } from '../ReportDatePicker';
import { ReportSelect } from '../ReportSelect';
import BurnDownChartFinishedSettings from './BurndownChartWidgetSettings';
import { Modal } from '@/components/utils/Modal';

interface BurndownChartWidgetProps {
  widget: ReportWidget;
  dashboard: { DashboardId: number; ReportId: number };
  filters: ReportFilters;
  onRemoveWidget: (widget: ReportWidget) => void;
  layers: LayerData[];
  onUpdateSettings?: (widgetId: number, newSettings: NewSettings) => void;
}

type ChartPer = 'Day' | 'Week' | 'Month';

interface AggregatedPoint {
  label: string;
  Ideal: number | null;
  Remaining: number | null;
  Predicted: number | null;
  Capacity: number | null;
}

function formatDateInput(d: Date): string
{
  return d.toISOString().split('T')[0] ?? '';
}

function isValidDate(dateStr: string | null | undefined): boolean
{
  if (!dateStr || dateStr.startsWith('0001-01-01')) return false;
  const d = new Date(dateStr);
  return !isNaN(d.getTime());
}

function computeShowVelocityWarning(data: BurndownData | null): boolean
{
  if (!data || data.BurndownChartData.length === 0) return false;
  const lastIdeal = data.BurndownChartData[data.BurndownChartData.length - 1]!.Ideal;
  return parseFloat(String(lastIdeal)) > 0;
}

function computeShowDueDateWarning(data: BurndownData | null, dueDate: string): boolean
{
  if (!data || data.BurndownChartData.length === 0) return false;
  const lastPoint = data.BurndownChartData[data.BurndownChartData.length - 1]!;
  const lastDateStr = lastPoint.CreatedAt.substring(0, 10);
  const dueDateStr = dueDate.substring(0, 10);
  return lastDateStr > dueDateStr;
}

function getDifferenceDays(startDate: string, dueDate: string): number
{
  const d1 = new Date(startDate.substring(0, 10)).getTime();
  const d2 = new Date(dueDate.substring(0, 10)).getTime();
  return (d2 - d1) / (1000 * 60 * 60 * 24);
}

export const BurndownChartWidget: React.FC<BurndownChartWidgetProps> = ({
  widget,
  dashboard,
  filters,
  onRemoveWidget,
  layers,
  onUpdateSettings,
}) =>
{
  const settings = widget.DataValuesJson?.Settings ?? {};
  const today = new Date();
  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);

  const [data, setData] = useState<BurndownData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const [selectedLayerId, setSelectedLayerId] = useState<number>(
    settings.LayerId ? Number(settings.LayerId) : 0,
  );
  const [startDate, setStartDate] = useState<string>(
    settings.StartDate ? String(settings.StartDate) : formatDateInput(today),
  );
  const [dueDate, setDueDate] = useState<string>(
    settings.DueDate ? String(settings.DueDate) : formatDateInput(nextWeek),
  );

  const [chartPer, setChartPer] = useState({
    value:
      widget.DataValuesJson.Settings.TypePeriodSelected == '0'
        ? 'Day'
        : widget.DataValuesJson.Settings.TypePeriodSelected === '1'
          ? 'Week'
          : 'Month',
    label:
      widget.DataValuesJson.Settings.TypePeriodSelected == '0'
        ? 'Day'
        : widget.DataValuesJson.Settings.TypePeriodSelected === '1'
          ? 'Week'
          : 'Month',
  });

  const menuRef = useRef<HTMLDivElement>(null);

  const [showSettings, setShowSettings] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const loadData = useCallback(async () =>
  {
    try
    {
      setLoading(true);
      setError(null);
      const result = await DashboardReportService.getBurndownChartFinished({
        dashboardId: dashboard.DashboardId,
        widgetId: widget.WidgetId,
        filters,
      });
      setData(result);
    }
    catch
    {
      setError('Failed to load burndown chart data.');
    }
    finally
    {
      setLoading(false);
    }
  }, [dashboard.DashboardId, widget.WidgetId, filters]);

  useEffect(() =>
  {
    loadData();
  }, [loadData]);

  useEffect(() =>
  {
    const handleClick = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleLayerOrDateChange = async (
    layerId: number,
    start: string,
    due: string,
    typePeriod: string,
  ) =>
  {
    try
    {
      setLoading(true);
      setError(null);
      await DashboardReportService.updateWidgetBurnDownChartFinishedSettings({
        ReportId: widget.ReportId,
        WidgetId: widget.WidgetId,
        LayerId: layerId,
        StartDate: start,
        DueDate: due,
        TypePeriodSelected: typePeriod,
      });
      await loadData();
    }
    catch
    {
      // ignore
    }
  };

  const handleLayerChange = (layerId: number) =>
  {
    setSelectedLayerId(layerId);
    handleLayerOrDateChange(
      layerId,
      startDate,
      dueDate,
      chartPer.value === 'Day' ? '0' : chartPer.value === 'Week' ? '1' : '2',
    );
  };

  const handleStartDateChange = (value: string) =>
  {
    setStartDate(value);
    handleLayerOrDateChange(
      selectedLayerId,
      value,
      dueDate,
      chartPer.value === 'Day' ? '0' : chartPer.value === 'Week' ? '1' : '2',
    );
  };

  const handleDueDateChange = (value: string) =>
  {
    setDueDate(value);
    handleLayerOrDateChange(
      selectedLayerId,
      startDate,
      value,
      chartPer.value === 'Day' ? '0' : chartPer.value === 'Week' ? '1' : '2',
    );
  };

  const handleTypePeriodChange = (value: { value: string; label: string }) =>
  {
    let type;
    if (value.label === 'Day')
    {
      type = '0';
    }
    else if (value.label === 'Week')
    {
      type = '1';
    }
    else type = '2';
    setChartPer(value);
    handleLayerOrDateChange(selectedLayerId, startDate, dueDate, type);
  };

  const handleDelete = async () =>
  {
    setMenuOpen(false);
    setDeleteModal(true);
  };

  const deleteWidget = async () =>
  {
    try
    {
      await DashboardReportService.deleteWidget(widget.ReportId, widget.WidgetId);
      onRemoveWidget(widget);
    }
    catch
    {
      // ignore
    }
  };

  const getDataChartNewSettings = (data: Record<string, unknown>, selectedLayer: number) =>
  {
    const newSettings: NewSettings = {
      LayerId: selectedLayer,
      DateFieldId: data.DateStartFieldId as number,
      AddDynamicGroup: data.AddDynamicGroup as boolean,
      CardType: data.CardType as string,
      ConfigValueID: data.ConfigValueID as number,
    };
    setShowSettings(false);
    setLoading(false);
    if (onUpdateSettings) onUpdateSettings(widget.WidgetId, newSettings);

    loadData();
  };

  // ─── buildLabels ─────────────────────────────────────────────────────────────
  // Identical logic to the legacy component.

  function buildLabels(
    days: number,
    startDate: string,
    dueDate: string,
    typePeriodSelected: ChartPer,
  ): string[]
  {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const pad = '00';

    const currentDate = new Date(startDate.substring(0, 10));
    currentDate.setDate(currentDate.getDate() + 1);
    const lastDate = new Date(dueDate.substring(0, 10));
    lastDate.setDate(lastDate.getDate() + 1);

    const labels: string[] = [];

    // ── Daily ──
    if (typePeriodSelected === 'Day')
    {
      while (currentDate <= lastDate)
      {
        labels.push(
          currentDate.getMonth() + 1 + '/' + (pad + currentDate.getDate()).slice(-pad.length),
        );
        currentDate.setDate(currentDate.getDate() + (days > 30 ? 3 : 1));
      }
      return labels;
    }

    // ── Weekly ──
    // Use startDate directly (not currentDate which is startDate+1) so that
    // the first weekEndDate matches what buildIdeal/buildRemaining compute.
    // The +1 day can cross a week boundary and cause a 1-week misalignment.
    if (typePeriodSelected === 'Week')
    {
      const edate = new Date(startDate.substring(0, 10));
      let weekEndDate: Date;

      if ((edate.getDate() === 14 || edate.getDate() === 15) && edate.getDay() === 1)
      {
        weekEndDate = edate;
      }
      else if (edate.getDate() === 1)
      {
        weekEndDate = edate;
      }
      else
      {
        const weekEnd = edate.setDate(edate.getDate() + (8 - edate.getDay()));
        weekEndDate = new Date(weekEnd);
      }

      const ldate = new Date(lastDate);
      const lweekEnd = ldate.setDate(ldate.getDate() + (8 - ldate.getDay()));
      const lastWeekEndDate = new Date(lweekEnd);

      while (weekEndDate <= lastWeekEndDate)
      {
        labels.push(
          weekEndDate.getMonth() + 1 + '/' + (pad + weekEndDate.getDate()).slice(-pad.length),
        );
        weekEndDate.setDate(weekEndDate.getDate() + 7);
      }
      return labels;
    }

    // ── Monthly ──
    // Use startDate's own month directly (not currentDate which is startDate+1)
    // so that labels align with build functions that also start from startDate's month.
    // The +1 day causes an off-by-one when startDate is the last day of a month.
    const monthOrigin = new Date(startDate.substring(0, 10));
    const sdate = new Date(monthOrigin.getFullYear(), monthOrigin.getMonth(), 1);
    while (sdate <= lastDate)
    {
      labels.push(monthNames[sdate.getMonth()] ?? '');
      sdate.setMonth(sdate.getMonth() + 1);
    }
    return labels;
  }

  // ─── buildIdeal ──────────────────────────────────────────────────────────────

  function buildIdeal(
    days: number,
    startDate: string,
    lastDate: string,
    data: BurndownData,
    typePeriodSelected: ChartPer,
  ): number[]
  {
    const points: number[] = [];
    let totalSuma = 0;

    // ── Daily ──
    if (typePeriodSelected === 'Day')
    {
      let cont = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        if (days > 30)
        {
          if (cont === index)
          {
            points.push(data.BurndownChartData[index]!.Ideal);
            cont += 3;
          }
        }
        else
        {
          points.push(data.BurndownChartData[index]!.Ideal);
        }
        totalSuma += data.BurndownChartData[index]!.Ideal;
      }
      return totalSuma === 0 ? [] : points;
    }

    // ── Weekly ──
    if (typePeriodSelected === 'Week')
    {
      const edate = new Date(startDate);
      let weekEndDate: Date;

      if ((edate.getDate() === 14 || edate.getDate() === 15) && edate.getDay() === 1)
      {
        weekEndDate = edate;
      }
      else if (edate.getDate() === 1)
      {
        weekEndDate = edate;
      }
      else
      {
        weekEndDate = new Date(edate.setDate(edate.getDate() + (8 - edate.getDay())));
      }

      const ldate = new Date(lastDate);
      const lastWeekEndDate = new Date(ldate.setDate(ldate.getDate() + (8 - ldate.getDay())));
      let indexFor = 0;
      const previousDate = new Date(startDate);
      previousDate.setDate(previousDate.getDate() + 7);

      while (weekEndDate <= lastWeekEndDate)
      {
        let totalValue = 0;
        indexFor = 0;
        for (let index = 0; index < data.BurndownChartData.length; index++)
        {
          if (indexFor <= data.BurndownChartData.length - 1)
          {
            const cDate = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
            cDate.setHours(0, 0, 0, 0);
            if (cDate <= previousDate && cDate <= weekEndDate)
            {
              totalValue = data.BurndownChartData[indexFor]!.Ideal;
              totalSuma += data.BurndownChartData[indexFor]!.Ideal;
              indexFor++;
            }
          }
        }

        points.push(totalValue);
        previousDate.setDate(previousDate.getDate() + 7);
        weekEndDate.setDate(weekEndDate.getDate() + 7);
      }
      return totalSuma === 0 ? [] : points;
    }

    // ── Monthly ──
    let fecha = new Date(startDate);
    let indexFor = 0;
    const NextMonthfecha = new Date(fecha);
    NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
    let ldate = new Date(lastDate);

    while (fecha <= ldate)
    {
      let totalValue = 0;
      indexFor = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        if (indexFor <= data.BurndownChartData.length - 1)
        {
          fecha = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
          ldate = new Date(lastDate);
          ldate.setHours(0, 0, 0, 0);
          if (fecha <= ldate)
          {
            if (indexFor <= data.BurndownChartData.length)
            {
              if (fecha.getMonth() !== NextMonthfecha.getMonth())
              {
                totalValue = data.BurndownChartData[indexFor]!.Ideal;
                totalSuma += data.BurndownChartData[indexFor]!.Ideal;
                indexFor++;
              }
              else
              {
                break;
              }
            }
            else
            {
              break;
            }
          }
          else
          {
            break;
          }
        }
        else
        {
          indexFor++;
          fecha = new Date(data.BurndownChartData[data.BurndownChartData.length - 1]!.CreatedAt);
        }
      }
      points.push(totalValue);
      NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
      if (indexFor > data.BurndownChartData.length - 1) break;
    }

    return totalSuma === 0 ? [] : points;
  }

  // ─── buildCapacity ───────────────────────────────────────────────────────────

  function buildCapacity(
    days: number,
    startDate: string,
    lastDate: string,
    data: BurndownData,
    typePeriodSelected: ChartPer,
  ): number[]
  {
    const points: number[] = [];
    let totalSuma = 0;

    // ── Daily ──
    if (typePeriodSelected === 'Day')
    {
      let cont = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        if (days > 30)
        {
          if (cont === index)
          {
            points.push(data.BurndownChartData[index]!.Capacity);
            cont += 3;
          }
        }
        else
        {
          points.push(data.BurndownChartData[index]!.Capacity);
        }
        totalSuma += data.BurndownChartData[index]!.Capacity;
      }
      return totalSuma === 0 ? [] : points;
    }

    // ── Weekly ──
    if (typePeriodSelected === 'Week')
    {
      const edate = new Date(startDate);
      let weekEndDate: Date;

      if ((edate.getDate() === 14 || edate.getDate() === 15) && edate.getDay() === 1)
      {
        weekEndDate = edate;
      }
      else if (edate.getDate() === 1)
      {
        weekEndDate = edate;
      }
      else
      {
        weekEndDate = new Date(edate.setDate(edate.getDate() + (8 - edate.getDay())));
      }

      const ldate = new Date(lastDate);
      const lastWeekEndDate = new Date(ldate.setDate(ldate.getDate() + (8 - ldate.getDay())));
      let indexFor = 0;
      const previousDate = new Date(startDate);
      previousDate.setDate(previousDate.getDate() + 7);

      while (weekEndDate <= lastWeekEndDate)
      {
        let totalValue = 0;
        indexFor = 0;
        for (let index = 0; index < data.BurndownChartData.length; index++)
        {
          if (indexFor <= data.BurndownChartData.length - 1)
          {
            const cDate = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
            cDate.setHours(0, 0, 0, 0);
            if (cDate <= previousDate && cDate <= weekEndDate)
            {
              totalValue = data.BurndownChartData[indexFor]!.Capacity;
              totalSuma += data.BurndownChartData[indexFor]!.Capacity;
              indexFor++;
            }
          }
        }
        points.push(totalValue);
        previousDate.setDate(previousDate.getDate() + 7);
        weekEndDate.setDate(weekEndDate.getDate() + 7);
      }
      return totalSuma === 0 ? [] : points;
    }

    // ── Monthly ──
    let fecha = new Date(startDate);
    let indexFor = 0;
    const NextMonthfecha = new Date(fecha);
    NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
    let ldate = new Date(lastDate);

    while (fecha <= ldate)
    {
      let totalValue = 0;
      indexFor = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        if (indexFor <= data.BurndownChartData.length - 1)
        {
          fecha = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
          ldate = new Date(lastDate);
          ldate.setHours(0, 0, 0, 0);
          if (fecha <= ldate)
          {
            if (indexFor <= data.BurndownChartData.length)
            {
              if (fecha.getMonth() !== NextMonthfecha.getMonth())
              {
                totalValue = data.BurndownChartData[indexFor]!.Ideal;
                totalSuma += data.BurndownChartData[indexFor]!.Ideal;
                indexFor++;
              }
              else
              {
                break;
              }
            }
            else
            {
              break;
            }
          }
          else
          {
            break;
          }
        }
        else
        {
          indexFor++;
          fecha = new Date(data.BurndownChartData[data.BurndownChartData.length - 1]!.CreatedAt);
        }
      }
      points.push(totalValue);
      NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
      if (indexFor > data.BurndownChartData.length - 1) break;
    }

    return totalSuma === 0 ? [] : points;
  }

  // ─── buildRemaining ──────────────────────────────────────────────────────────

  function buildRemaining(
    days: number,
    startDate: string,
    _lastDate: string,
    data: BurndownData,
    typePeriodSelected: ChartPer,
  ): (number | null)[]
  {
    const points: (number | null)[] = [];

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    // Cap remaining at today (same as legacy)
    const effectiveLastDate = currentDate;

    // ── Daily ──
    if (typePeriodSelected === 'Day')
    {
      let cont = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        const pointDate = new Date(data.BurndownChartData[index]!.CreatedAt.substring(0, 10));
        pointDate.setDate(pointDate.getDate() + 1);
        pointDate.setHours(0, 0, 0, 0);

        const isDatePredicted = pointDate.getTime() > currentDate.getTime();

        if (days > 30)
        {
          if (cont === index)
          {
            points.push(
              parseInt(String(data.BurndownChartData[index]!.Remaining)) >= 0 && !isDatePredicted
                ? data.BurndownChartData[index]!.Remaining
                : null,
            );
            cont += 3;
          }
        }
        else
        {
          points.push(
            parseInt(String(data.BurndownChartData[index]!.Remaining)) >= 0 && !isDatePredicted
              ? data.BurndownChartData[index]!.Remaining
              : null,
          );
        }
      }
      return points;
    }

    // ── Weekly ──
    if (typePeriodSelected === 'Week')
    {
      const edate = new Date(startDate);
      let weekEndDate: Date;

      if ((edate.getDate() === 14 || edate.getDate() === 15) && edate.getDay() === 1)
      {
        weekEndDate = edate;
      }
      else if (edate.getDate() === 1)
      {
        weekEndDate = edate;
      }
      else
      {
        weekEndDate = new Date(edate.setDate(edate.getDate() + (8 - edate.getDay())));
      }

      const previousDate = new Date(startDate);
      previousDate.setDate(previousDate.getDate() + 7);

      while (weekEndDate <= effectiveLastDate)
      {
        let totalValue = 0;
        let indexFor = 0;
        for (let index = 0; index < data.BurndownChartData.length; index++)
        {
          if (indexFor <= data.BurndownChartData.length)
          {
            const cDate = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
            cDate.setHours(0, 0, 0, 0);
            previousDate.setHours(0, 0, 0, 0);
            if (cDate <= previousDate)
            {
              if (data.BurndownChartData[indexFor]!.Remaining > 0)
              {
                totalValue = data.BurndownChartData[indexFor]!.Remaining;
                indexFor++;
              }
            }
          }
        }
        if (totalValue > 0) points.push(totalValue);
        previousDate.setDate(previousDate.getDate() + 7);
        weekEndDate.setDate(weekEndDate.getDate() + 7);
      }
      return points;
    }

    // ── Monthly ──
    let fecha = new Date(startDate);
    const currentDate2 = new Date(startDate);
    currentDate2.setHours(0, 0, 0, 0);
    const NextMonthfecha = new Date(currentDate2);
    NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
    let mLastDate = new Date(effectiveLastDate);

    while (fecha <= mLastDate)
    {
      let totalValue = 0;
      let indexFor = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        if (indexFor <= data.BurndownChartData.length - 1)
        {
          fecha = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
          mLastDate = new Date(mLastDate);
          mLastDate.setHours(0, 0, 0, 0);
          if (fecha <= mLastDate)
          {
            if (fecha.getMonth() !== NextMonthfecha.getMonth())
            {
              totalValue = data.BurndownChartData[indexFor]!.Remaining;
              indexFor++;
            }
            else
            {
              break;
            }
          }
          else
          {
            break;
          }
        }
        else
        {
          indexFor++;
          fecha = new Date(data.BurndownChartData[data.BurndownChartData.length - 1]!.CreatedAt);
        }
      }
      if (totalValue > 0) points.push(totalValue);
      NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
      if (indexFor > data.BurndownChartData.length - 1) break;
    }

    return points;
  }

  // ─── buildPredicted ──────────────────────────────────────────────────────────

  function buildPredicted(
    days: number,
    startDate: string,
    lastDate: string,
    data: BurndownData,
    typePeriodSelected: ChartPer,
    totalRemaining: (number | null)[],
    dueDate: string,
  ): (number | null)[]
  {
    const points: (number | null)[] = [];

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const previousDate = new Date(currentDate);

    const newDueDate = new Date(dueDate.substring(0, 10));
    newDueDate.setHours(0, 0, 0, 0);
    newDueDate.setDate(newDueDate.getDate() + 1);

    const effectiveCurrent = newDueDate <= currentDate ? newDueDate : currentDate;
    let counter = 0;

    // Pre-fill nulls for days > 30
    if (days > 30)
    {
      const ppredicted: null[] = [];
      for (let i = 0; i < totalRemaining.length; i++)
      {
        if ((totalRemaining[i] ?? 0) > 0) ppredicted.push(null);
      }
      for (let i = 0; i < ppredicted.length - 1; i++) points.push(null);
    }

    // ── Daily ──
    if (typePeriodSelected === 'Day')
    {
      let count = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        const pointDate = new Date(data.BurndownChartData[index]!.CreatedAt.substring(0, 10));
        pointDate.setDate(pointDate.getDate() + 1);
        pointDate.setHours(0, 0, 0, 0);

        if (pointDate >= effectiveCurrent)
        {
          if (days > 30)
          {
            if (count === index)
            {
              if (
                parseInt(String(data.BurndownChartData[index]!.Predicted)) === 0 &&
                counter === 0
              )
              {
                count += 3;
                points.push(data.BurndownChartData[index]!.Predicted);
              }
              else
              {
                points.push(data.BurndownChartData[index]!.Predicted);
                count += 3;
              }
            }
          }
          else
          {
            if (parseInt(String(data.BurndownChartData[index]!.Predicted)) === 0 && counter === 0)
            {
              counter++;
              points.push(data.BurndownChartData[index]!.Predicted);
            }
            else
            {
              points.push(data.BurndownChartData[index]!.Predicted);
            }
          }
        }
        else
        {
          if (days > 30)
          {
            if (count === index) count += 3;
          }
          else
          {
            points.push(null);
          }
        }
      }
      return points;
    }

    // ── Weekly ──
    if (typePeriodSelected === 'Week')
    {
      for (let i = 0; i < totalRemaining.length - 1; i++) points.push(null);
      const lastValue = totalRemaining[totalRemaining.length - 1] ?? 0;
      points.push(lastValue);

      const edate = new Date(effectiveCurrent);
      let weekEndDate: Date;

      if ((edate.getDate() === 14 || edate.getDate() === 15) && edate.getDay() === 1)
      {
        weekEndDate = edate;
      }
      else if (edate.getDate() === 1)
      {
        weekEndDate = edate;
      }
      else
      {
        weekEndDate = new Date(edate.setDate(edate.getDate() + (8 - edate.getDay())));
      }

      const ldate = new Date(lastDate);
      const lastWeekEndDate = new Date(ldate.setDate(ldate.getDate() + (8 - ldate.getDay())));
      previousDate.setDate(previousDate.getDate() + 7);

      while (previousDate <= lastWeekEndDate)
      {
        let totalValue = 0;
        let indexFor = 0;
        for (let index = 0; index < data.BurndownChartData.length; index++)
        {
          if (indexFor <= data.BurndownChartData.length - 1)
          {
            const sDate = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
            sDate.setHours(0, 0, 0, 0);
            previousDate.setHours(0, 0, 0, 0);

            const pointDate = new Date(
              data.BurndownChartData[indexFor]!.CreatedAt.substring(0, 10),
            );
            pointDate.setDate(pointDate.getDate() + 1);
            pointDate.setHours(0, 0, 0, 0);

            if (pointDate >= effectiveCurrent)
            {
              if (sDate <= previousDate)
              {
                if (totalValue === 0) totalValue = data.BurndownChartData[indexFor]!.Predicted;
                indexFor++;
              }
            }
            else
            {
              indexFor++;
            }
          }
        }
        if (totalValue > 0) points.push(totalValue);
        previousDate.setDate(previousDate.getDate() + 7);
        weekEndDate.setDate(weekEndDate.getDate() + 7);
      }
      return points;
    }

    // ── Monthly ──
    for (let i = 0; i < totalRemaining.length - 1; i++) points.push(null);

    let fecha = new Date(startDate);
    fecha.setHours(0, 0, 0, 0);

    const NextMonthfecha = new Date(effectiveCurrent);
    NextMonthfecha.setHours(0, 0, 0, 0);
    NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
    const mLastDate = new Date(lastDate);
    mLastDate.setHours(0, 0, 0, 0);

    while (fecha <= mLastDate)
    {
      let totalValue = 0;
      let indexFor = 0;
      for (let index = 0; index < data.BurndownChartData.length; index++)
      {
        if (indexFor <= data.BurndownChartData.length - 1)
        {
          fecha = new Date(data.BurndownChartData[indexFor]!.CreatedAt);
          const pointDate = new Date(data.BurndownChartData[indexFor]!.CreatedAt.substring(0, 10));
          pointDate.setDate(pointDate.getDate() + 1);
          pointDate.setHours(0, 0, 0, 0);

          if (pointDate >= effectiveCurrent)
          {
            if (fecha < NextMonthfecha)
            {
              totalValue = data.BurndownChartData[indexFor]!.Predicted;
              indexFor++;
            }
            else
            {
              indexFor++;
              break;
            }
          }
          else
          {
            indexFor++;
          }
        }
        else
        {
          break;
        }
      }
      if (totalValue > 0) points.push(totalValue);
      NextMonthfecha.setMonth(NextMonthfecha.getMonth() + 1);
      if (indexFor > data.BurndownChartData.length - 1) break;
    }

    return points;
  }

  // ─── assembleChartData ───────────────────────────────────────────────────────
  // Builds the array of { label, Ideal, Remaining, Predicted, Capacity } objects
  // that Recharts consumes.
  //
  // Daily mode uses the existing build* functions (legacy-compatible).
  // Weekly and Monthly use a direct grouping approach to avoid the subtle
  // off-by-one alignment bugs in the complex nested-loop build* functions.

  const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  function assembleChartData(
    data: BurndownData,
    typePeriodSelected: ChartPer,
    startDate: string,
    dueDate: string,
  ): AggregatedPoint[]
  {
    const lastEntry = data.BurndownChartData[data.BurndownChartData.length - 1];
    if (!lastEntry) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ── Daily: keep legacy build* functions ─────────────────────────────────────
    if (typePeriodSelected === 'Day')
    {
      const lastDate = lastEntry.CreatedAt;
      const days = getDifferenceDays(startDate, dueDate);
      const labels = buildLabels(days, startDate, dueDate, typePeriodSelected);
      const ideals = buildIdeal(days, startDate, lastDate, data, typePeriodSelected);
      const remaining = buildRemaining(days, startDate, lastDate, data, typePeriodSelected);
      const capacity = buildCapacity(days, startDate, lastDate, data, typePeriodSelected);
      const predicted =
        Math.abs(data.Velocity) === data.Velocity
          ? buildPredicted(days, startDate, lastDate, data, typePeriodSelected, remaining, dueDate)
          : labels.map(() => null);
      return labels.map((label, i) => ({
        label,
        Ideal: ideals[i] ?? null,
        Remaining: remaining[i] ?? null,
        Predicted: predicted[i] ?? null,
        Capacity: capacity[i] ?? null,
      }));
    }

    // ── Monthly: group BurndownChartData by calendar month ──────────────────────
    if (typePeriodSelected === 'Month')
    {
      const origin = new Date(startDate.substring(0, 10));
      const duePlusOne = new Date(dueDate.substring(0, 10));
      duePlusOne.setDate(duePlusOne.getDate() + 1);

      const result: AggregatedPoint[] = [];
      const cur = new Date(origin.getFullYear(), origin.getMonth(), 1);

      while (cur <= duePlusOne)
      {
        const nextMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        const label = MONTH_NAMES[cur.getMonth()] ?? '';

        let ideal: number | null = null;
        let remaining: number | null = null;
        let predicted: number | null = null;
        let capacity: number | null = null;

        for (const pt of data.BurndownChartData)
        {
          const d = new Date(pt.CreatedAt);
          d.setHours(0, 0, 0, 0);
          if (d >= cur && d < nextMonth)
          {
            ideal = pt.Ideal ?? null;
            capacity = pt.Capacity ?? null;
            if (d <= today)
            {
              remaining = pt.Remaining > 0 ? pt.Remaining : null;
              predicted = null;
            }
            else
            {
              predicted = pt.Predicted > 0 ? pt.Predicted : null;
            }
          }
        }

        result.push({
          label,
          Ideal: ideal,
          Remaining: remaining,
          Predicted: predicted,
          Capacity: capacity,
        });
        cur.setMonth(cur.getMonth() + 1);
      }
      return result;
    }

    // ── Weekly: group BurndownChartData by week boundary ────────────────────────
    const pad = '00';
    const edate = new Date(startDate.substring(0, 10));
    let weekEndDate: Date;
    if ((edate.getDate() === 14 || edate.getDate() === 15) && edate.getDay() === 1)
    {
      weekEndDate = new Date(edate);
    }
    else if (edate.getDate() === 1)
    {
      weekEndDate = new Date(edate);
    }
    else
    {
      weekEndDate = new Date(edate.setDate(edate.getDate() + (8 - edate.getDay())));
    }

    const ldate = new Date(dueDate.substring(0, 10));
    ldate.setDate(ldate.getDate() + 1);
    const lastWeekEndDate = new Date(ldate.setDate(ldate.getDate() + (8 - ldate.getDay())));

    const result: AggregatedPoint[] = [];
    let prevWeekEnd = new Date(0);

    while (weekEndDate <= lastWeekEndDate)
    {
      const label = `${weekEndDate.getMonth() + 1}/${(pad + weekEndDate.getDate()).slice(-pad.length)}`;
      const weekEnd = new Date(weekEndDate);

      let ideal: number | null = null;
      let remaining: number | null = null;
      let predicted: number | null = null;
      let capacity: number | null = null;

      for (const pt of data.BurndownChartData)
      {
        const d = new Date(pt.CreatedAt);
        d.setHours(0, 0, 0, 0);
        if (d > prevWeekEnd && d <= weekEnd)
        {
          ideal = pt.Ideal ?? null;
          capacity = pt.Capacity ?? null;
          if (d <= today)
          {
            remaining = pt.Remaining > 0 ? pt.Remaining : null;
            predicted = null;
          }
          else
          {
            predicted = pt.Predicted > 0 ? pt.Predicted : null;
          }
        }
      }

      result.push({
        label,
        Ideal: ideal,
        Remaining: remaining,
        Predicted: predicted,
        Capacity: capacity,
      });
      prevWeekEnd = new Date(weekEnd);
      weekEndDate.setDate(weekEndDate.getDate() + 7);
    }
    return result;
  }

  // const chartData = data ? aggregateData(data.BurndownChartData, chartPer.value, startDate, dueDate) : [];
  const chartData: AggregatedPoint[] =
    data && data.BurndownChartData.length > 0
      ? assembleChartData(data, chartPer.value as ChartPer, startDate, dueDate)
      : [];
  const showVelocityWarning = computeShowVelocityWarning(data);
  const showDueDateWarning = computeShowDueDateWarning(data, dueDate);
  const isDark = document.documentElement.classList.contains('dark');

  return (
    <>
      <div className="styled-widget-container shadow rounded mb-2">
        {/* Header */}
        <div className="widget-header flex items-center justify-between px-3 py-2">
          <span className="text-sm font-medium text-gray-700 dark:text-[#a9a093]">
            {widget.Description || 'Burndown Chart'}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-[#3a3c3c] rounded dark:text-[#a9a093]"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Widget menu"
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-7 z-50 bg-white dark:bg-[#333535] border border-gray-200 dark:border-[#4a4c4c] rounded shadow-md min-w-[140px]">
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093] disabled:cursor-not-allowed"
                  disabled={selectedLayerId === 0}
                  onClick={() => setShowSettings(true)}
                >
                  Settings
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] text-red-600"
                  onClick={handleDelete}
                >
                  Delete Widget
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="burndown-header flex justify-center">
          <ReportSelect
            label="Layer:"
            value={
              [
                { value: 0, label: 'All Layers' },
                ...layers.map(l => ({ value: l.LayerId, label: l.Name })),
              ].find(x => x.value === selectedLayerId) ?? null
            }
            onChange={v =>
            {
              if (v)
                handleLayerChange(Number((v as { value: string | number; label: string }).value));
            }}
            options={[
              { value: 0, label: 'All Layers' },
              ...layers.map(l => ({ value: l.LayerId, label: l.Name })),
            ]}
            width={140}
          />
          <ReportDatePicker label="Start:" value={startDate} onChange={handleStartDateChange} />
          <ReportDatePicker label="Due:" value={dueDate} onChange={handleDueDateChange} />
          <ReportSelect
            label="Chart per:"
            value={chartPer}
            onChange={v =>
            {
              if (v) handleTypePeriodChange(v as { value: string; label: string });
            }}
            options={[
              { value: 'Day', label: 'Day' },
              { value: 'Week', label: 'Week' },
              { value: 'Month', label: 'Month' },
            ]}
            width={100}
          />
        </div>

        {/* Content */}
        <div className="widget-content p-4">
          {loading && (
            <p className="report-widget-feedback-message text-xs italic text-center text-gray-400">
              Loading chart data...
            </p>
          )}
          {error && (
            <p className="report-widget-feedback-message text-xs italic text-red-400 text-center">
              {error}
            </p>
          )}
          {!showSettings && !loading && !error && data && (
            <>
              {chartData.length === 0 ? (
                <p className="report-widget-feedback-message text-xs italic text-gray-400">
                  No data available for the selected range.
                </p>
              ) : (
                <>
                  {showVelocityWarning && (
                    <p className="report-widget-feedback-message text-xs italic text-amber-500 mb-2 text-center">
                      The velocity is slow, the last point is not visible because the last date is
                      out the date range.
                    </p>
                  )}
                  {showDueDateWarning && (
                    <div>
                      <p className="burndownchart_RedLabel">
                        {' '}
                        Due date has been extended based on the logged hrs previous day.
                      </p>
                    </div>
                  )}
                  {data.realVelocity <= 0 && (
                    <p className="report-widget-feedback-message text-xs italic text-red-400 text-center">
                      Based on the logged hrs previous day.
                    </p>
                  )}
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={isDark ? '#3a3c3c' : '#f0f0f0'}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: isDark ? '#6e6860' : '#999' }}
                        axisLine={{ stroke: isDark ? '#4a4c4c' : '#e0e0e0' }}
                        tickLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: isDark ? '#6e6860' : '#999' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          fontSize: 11,
                          backgroundColor: isDark ? '#333535' : '#fff',
                          borderColor: isDark ? '#4a4c4c' : '#e5e5e5',
                          color: isDark ? '#a9a093' : '#333',
                        }}
                        labelStyle={{ fontWeight: 600 }}
                      />
                      <Line
                        type="linear"
                        dataKey="Ideal"
                        stroke="#b0b0b0"
                        strokeWidth={1.5}
                        dot={{ r: 3, fill: '#b0b0b0', strokeWidth: 0 }}
                        activeDot={{ r: 4 }}
                        name="Ideal"
                      />
                      <Line
                        type="linear"
                        dataKey="Predicted"
                        stroke="#f26c4f"
                        strokeWidth={1.5}
                        dot={{ r: 3, fill: '#f26c4f', strokeWidth: 0 }}
                        activeDot={{ r: 4 }}
                        name="Predicted"
                      />
                      <Line
                        type="linear"
                        dataKey="Remaining"
                        stroke="#62accb"
                        strokeWidth={1.5}
                        dot={{ r: 3, fill: '#62accb', strokeWidth: 0 }}
                        activeDot={{ r: 4 }}
                        name="Remaining"
                      />
                      <Line
                        type="linear"
                        dataKey="Capacity"
                        stroke="#6e6e6e"
                        strokeWidth={1.5}
                        dot={{ r: 3, fill: '#6e6e6e', strokeWidth: 0 }}
                        activeDot={{ r: 4 }}
                        name="Capacity"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </>
              )}
              <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-[#6e6860] flex-wrap">
                <span>
                  Total:{' '}
                  <strong className="text-gray-700 dark:text-[#a9a093]">{data.TotalTasks}</strong>
                </span>
                <span>
                  Velocity:{' '}
                  <strong className="text-gray-700 dark:text-[#a9a093]">{data.Velocity}</strong>
                </span>
                {isValidDate(data.PredictedDate) && (
                  <span>
                    Predicted Date:{' '}
                    <strong className="text-gray-700 dark:text-[#a9a093]">
                      {new Date(data.PredictedDate!).toLocaleDateString()}
                    </strong>
                  </span>
                )}
              </div>
            </>
          )}
          {showSettings && (
            <BurnDownChartFinishedSettings
              startDate={startDate}
              dueDate={dueDate}
              widgetId={widget.WidgetId}
              dashboard={{ ...dashboard, ConfigValueId: 0, ProjectId: 0 }}
              settings={settings}
              LayerId={selectedLayerId}
              onCancel={() => setShowSettings(false)}
              onSave={(data, layerId) =>
                getDataChartNewSettings(data as unknown as Record<string, unknown>, layerId)
              }
            />
          )}
        </div>
      </div>
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Widget"
        preventCloseClickOut
      >
        <p>Are you sure you want to delete it?</p>
        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            onClick={() =>
            {
              deleteWidget();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
          >
            Ok
          </button>
          <button
            type="button"
            onClick={() => setDeleteModal(false)}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};

export default BurndownChartWidget;
